import { fetchWithAuth } from './api';
import { ensureOk, okJson } from './httpError';

export interface PersonFingerprint {
  id: string;
  customer_id: string;
  person_id: string;
  finger_type: number;
  finger_label: string;
  template_b64?: string;
  enrolled_device_id?: string;
  enrolled_at?: string;
  created_at: string;
  updated_at: string;
}

export const FINGER_LABELS: Record<number, string> = {
  0: 'Indicador Direito',
  1: 'Indicador Esquerdo',
  2: 'Polegar Direito',
  3: 'Polegar Esquerdo',
  4: 'Médio Direito',
  5: 'Médio Esquerdo',
  6: 'Anelar Direito',
  7: 'Anelar Esquerdo',
};

const base = (customerId: string, personId: string) =>
  `/api/v1/customers/${customerId}/people/${personId}/fingerprints`;

export const fingerprintService = {
  async list(customerId: string, personId: string): Promise<PersonFingerprint[]> {
    const res = await fetchWithAuth(base(customerId, personId));
    // Unchecked, an error body hit `?? []` and rendered as "no fingerprints enrolled".
    const data = await okJson<any>(res, 'Erro ao carregar as digitais');
    return data.fingerprints ?? [];
  },

  async saveTemplate(
    customerId: string,
    personId: string,
    params: { fingerType: number; fingerLabel?: string; templateB64: string; enrolledDeviceId?: string }
  ): Promise<PersonFingerprint> {
    const res = await fetchWithAuth(`${base(customerId, personId)}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        finger_type: params.fingerType,
        finger_label: params.fingerLabel,
        template_b64: params.templateB64,
        enrolled_device_id: params.enrolledDeviceId,
      }),
    });
    const data = await okJson<any>(res, 'Erro ao salvar digital');
    return data.fingerprint;
  },

  async cloudEnroll(
    customerId: string,
    personId: string,
    params: { deviceId: string; fingerType: number; fingerLabel?: string }
  ): Promise<PersonFingerprint> {
    const res = await fetchWithAuth(`${base(customerId, personId)}/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_id: params.deviceId,
        finger_type: params.fingerType,
        finger_label: params.fingerLabel,
      }),
    });
    const data = await okJson<any>(res, 'Erro ao matricular digital');
    return data.fingerprint;
  },

  async importFromDevice(
    customerId: string,
    personId: string,
    deviceId: string,
    employeeNo?: string
  ): Promise<{ fingerprints: PersonFingerprint[]; imported_count: number }> {
    const res = await fetchWithAuth(`${base(customerId, personId)}/import-from-device`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId, employee_no: employeeNo }),
    });
    const data = await okJson<any>(res, 'Erro ao importar digital');
    return { fingerprints: data.fingerprints ?? [], imported_count: data.imported_count ?? 0 };
  },

  /**
   * Persist fingerprint templates the caller already fetched directly from the device
   * (Electron local path — used for devices only reachable on the LAN, which iot-connect
   * running in AWS cannot reach). Upserts by finger_type, same as importFromDevice.
   */
  async importDirect(
    customerId: string,
    personId: string,
    templates: Array<{ finger_type: number; template_b64: string }>
  ): Promise<{ fingerprints: PersonFingerprint[]; imported_count: number }> {
    const res = await fetchWithAuth(`${base(customerId, personId)}/import-direct`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templates }),
    });
    const data = await okJson<any>(res, 'Erro ao importar digital');
    return { fingerprints: data.fingerprints ?? [], imported_count: data.imported_count ?? 0 };
  },

  async deleteFingerprint(customerId: string, personId: string, fpId: string): Promise<void> {
    const res = await fetchWithAuth(`${base(customerId, personId)}/${fpId}`, { method: 'DELETE' });
    await ensureOk(res, 'Erro ao excluir digital');
  },
};
